"use client";

import React, { useState } from "react";
import toast from "react-hot-toast";
import { alpha, Avatar, Box, Typography, styled } from "@mui/material";
import {
  useAddCandidateProject,
  useUpdateCandidateProject,
  useRemoveCandidateProject,
  useReorderCandidateProjects,
} from "@lib/candidates/useProjectMutations";
import ReorderButtons from "@components/shared/ReorderButtons";
import {
  RightColumn,
  SectionHeader,
  SectionContent,
  Label,
} from "./styles/SharedStyles";
import EditButton from "@components/shared/EditButton";
import MarkdownList from "@components/shared/MarkdownList";
import EditProjects from "../modals/EditProjects";
import { ProfileSkeleton } from "./skeletons";

const AddProjectWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
  height: "50px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: "4px",
  cursor: "pointer",

  "&:hover": {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[100],
  },

  "& span": {
    color: theme.palette.secondary.main,
  },
}));

const Project = styled(Box)(({ theme }) => ({
  display: "flex",
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: "relative",

  "&.no-border": {
    borderBottom: "none",
    marginBottom: 0,
    paddingBottom: 0,
  },
}));

const ProjectHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginBottom: theme.spacing(1),
  paddingTop: "7px",
}));

const ProjectDescription = styled(Box)({
  lineHeight: 1.6,
});

const ProjectDetails = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "top",
  gap: theme.spacing(2),
}));

const ProjectProperties = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  fontSize: 14,

  "& p": {
    margin: 0,
  },
}));

const ProjectImage = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "themeColor",
})(({ theme, themeColor }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  overflow: "hidden",
  alignSelf: "flex-start",
  backgroundColor: themeColor
    ? alpha(themeColor, 0.4)
    : theme.palette.primary.lighter,
  fontSize: "12px",
  fontWeight: "600",
  color: theme.palette.black?.main || theme.palette.text.primary,
}));

const Projects = ({ candidate, isImporting }) => {
  const [editingProject, setEditingProject] = useState(null);
  const addProject = useAddCandidateProject();
  const updateProject = useUpdateCandidateProject();
  const removeProject = useRemoveCandidateProject();
  const reorderProjects = useReorderCandidateProjects();

  const handleEditProject = (project) => {
    setEditingProject(project);
  };

  const handleUpdateProject = (updatedProject) => {
    const toastId = toast.loading("Saving...");
    updateProject.mutate(
      { candidate, project: updatedProject },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to save");
        },
      }
    );
  };

  const handleAddProject = (event) => {
    const toastId = toast.loading("Adding...");
    event.preventDefault();
    addProject.mutate(
      {
        candidate,
        project: { name: "New project", role: "", description: "" },
      },
      {
        onSuccess: (data) => {
          toast.dismiss(toastId);
          setEditingProject(data.data.project);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to add project");
        },
      }
    );
  };

  const handleReorder = (index, direction) => {
    const projects = [...candidate.projects];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [projects[index], projects[newIndex]] = [projects[newIndex], projects[index]];
    const toastId = toast.loading("Saving changes...");
    reorderProjects.mutate(
      {
        candidate,
        order: projects.map((p) => p.id),
      },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to reorder projects");
        },
      }
    );
  };

  const handleDeleteProject = (projectId) => {
    const toastId = toast.loading("Removing project...");
    removeProject.mutate(
      { candidate, projectId },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to remove project");
        },
      }
    );
  };

  return (
    <Box id="candidate-section-projects">
      <SectionContent>
        <Box>
          <RightColumn>
            <SectionHeader>
              <Typography variant="h2">Projects</Typography>
            </SectionHeader>
            {candidate.projects?.map((project, index) => (
              <Project
                key={project.id}
                className={
                  index === candidate.projects.length - 1 && !isImporting
                    ? "no-border"
                    : ""
                }
              >
                <ReorderButtons
                  onMoveUp={index > 0 ? () => handleReorder(index, "up") : null}
                  onMoveDown={
                    index < candidate.projects.length - 1
                      ? () => handleReorder(index, "down")
                      : null
                  }
                  sx={{ position: "absolute", top: "-8px", right: 96, height: 36 }}
                />
                <EditButton
                  onEdit={() => handleEditProject(project)}
                  sx={{ top: 0, right: 0 }}
                />
                <Label sx={{ lineHeight: 2.2 }}>Project details:</Label>
                <ProjectDetails>
                  <ProjectImage src={project.image} alt={project.name}>
                    {project.name?.substring(0, 2) ?? ""}
                  </ProjectImage>
                  <ProjectProperties>
                    <ProjectHeader>
                      <p style={{ maxWidth: "calc(100% - 160px)" }}>
                        {project.name}
                      </p>
                      <p>{project.role}</p>
                    </ProjectHeader>
                    <ProjectDescription>
                      <MarkdownList content={project.description} />
                    </ProjectDescription>
                  </ProjectProperties>
                </ProjectDetails>
              </Project>
            ))}
            {isImporting && (
              <Project className="no-border">
                <Label sx={{ lineHeight: 2.2 }}>Project details:</Label>
                <ProfileSkeleton />
              </Project>
            )}
            <AddProjectWrapper onClick={handleAddProject}>
              <span>Add new project +</span>
            </AddProjectWrapper>
          </RightColumn>
        </Box>
      </SectionContent>
      <EditProjects
        key={editingProject?.id}
        open={editingProject !== null}
        onClose={() => setEditingProject(null)}
        project={editingProject}
        onUpdate={handleUpdateProject}
        onDelete={handleDeleteProject}
        candidate={candidate}
      />
    </Box>
  );
};

export default Projects;
